import { roundMoney } from '../../utils/money';

/**
 * Review 2026-10-07: on an INBOUND truck the freight goes to the ZD batches
 * (and through FIFO to the customer margin), so a save that moves freight from
 * one ZD to another must say so before it happens — in złoty, per ZD.
 * Pure: the form passes the preview rows of the new split and the saved
 * freight of each ZD line; an unresolved saved share (null) silences it.
 */
export interface FreightShiftLine {
  /** ZD line id (preview row key). */
  id: string;
  supplierOrderId: string;
  supplierOrderLabel: string;
  /** New split, PLN; null = unknown (no rate yet). */
  newPLN: number | null;
}

export interface SavedZdFreight {
  supplierOrderId: string;
  supplierOrderLabel: string;
  /** Saved freight of every line of this ZD on the truck, PLN; null = unknown. */
  linePLN: Array<number | null>;
}

export interface SupplierFreightShift {
  supplierOrderId: string;
  label: string;
  beforePLN: number;
  afterPLN: number;
  differencePLN: number;
}

export function supplierFreightShifts(
  lines: FreightShiftLine[],
  saved: SavedZdFreight[],
): SupplierFreightShift[] {
  // Without the new amounts (no rate yet) there is nothing to compare.
  if (lines.some((line) => line.newPLN === null)) return [];
  const after = new Map<string, number>();
  const labels = new Map<string, string>();
  lines.forEach((line) => {
    after.set(line.supplierOrderId, roundMoney((after.get(line.supplierOrderId) ?? 0) + (line.newPLN ?? 0)));
    labels.set(line.supplierOrderId, line.supplierOrderLabel);
  });
  // A saved ZD whose freight is unresolved (null: manual key, unresolved
  // quantities) gives no baseline — comparing with 0 would invent a shift on
  // every save. A ZD new on the truck has no saved share: 0 zł, and it is
  // listed — adding it is what moves the money (review F4).
  if (saved.some((zd) => zd.linePLN.some((value) => value === null))) return [];
  const before = new Map<string, number>();
  saved.forEach((zd) => {
    before.set(zd.supplierOrderId, roundMoney(zd.linePLN.reduce<number>((sum, value) => sum + (value ?? 0), 0)));
    labels.set(zd.supplierOrderId, zd.supplierOrderLabel);
  });
  const ids = Array.from(new Set(Array.from(before.keys()).concat(Array.from(after.keys()))));
  const totalBefore = Array.from(before.values()).reduce((sum, value) => sum + value, 0);
  const totalAfter = Array.from(after.values()).reduce((sum, value) => sum + value, 0);
  if (!(totalBefore > 0) || !(totalAfter > 0)) return [];
  // A new rate alone scales every ZD alike — not a shift. Only a ZD whose part
  // of the truck changed (beyond a grosz per ZD of rounding) is reported.
  const scale = totalAfter / totalBefore;
  const moved = ids.some((id) => Math.abs((after.get(id) ?? 0) - (before.get(id) ?? 0) * scale) >= 0.02);
  if (!moved) return [];
  return ids.flatMap((id) => {
    const beforePLN = before.get(id) ?? 0;
    const afterPLN = after.get(id) ?? 0;
    const differencePLN = roundMoney(afterPLN - beforePLN);
    return Math.abs(differencePLN) >= 0.01
      ? [{ supplierOrderId: id, label: labels.get(id) || id, beforePLN, afterPLN, differencePLN }]
      : [];
  });
}
